import { handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  UpdateJobPositionMutation,
  UpdateJobPositionMutationVariables,
  useGetJobPositionByIdLazyQuery,
  useUpdateJobPositionMutation,
} from "@graphql/generated";
import { useOrganizationStore } from "@store/useOrganizationStore";
import toast from "react-hot-toast";
import { ICreateJobPositionValues } from "./useCreateJobPosition";

interface IUseUpdateJobPositionParams {
  handleNextStep: () => void;
  values: ICreateJobPositionValues;
  id: string;
}

export const useUpdateJobPosition = () => {
  const { selectedOrganization } = useOrganizationStore();
  const [updateJobPosition, { loading }] = useUpdateJobPositionMutation();
  const [getJobPosition] = useGetJobPositionByIdLazyQuery();

  const handleSubmit = async ({ handleNextStep, values, id }: IUseUpdateJobPositionParams) => {
    if (!selectedOrganization) {
      toast.error("Please select an organization");
      return;
    }

    try {
      const jobPosition = await getJobPosition({ variables: { id }, fetchPolicy: "no-cache" });
      const currentJobPosition = jobPosition.data?.organization?.jobPosition?.get;

      if (!currentJobPosition) {
        toast.error("Failed to fetch job post data");
        return;
      }

      await handleApiRequest<UpdateJobPositionMutation, UpdateJobPositionMutationVariables>({
        apiFunction: updateJobPosition,
        variables: {
          id,
          performanceExpectation: currentJobPosition.performanceExpectation || "",
          description: currentJobPosition.description || "",
          ...values,
          degrees:
            values.degrees?.map((degree) => (typeof degree === "string" ? degree : degree.value)) ??
            currentJobPosition.degrees ??
            [],
          languages:
            values.languages?.map((language) => (typeof language === "string" ? language : language.value)) ??
            currentJobPosition.languages ??
            [],
          nativeLanguages:
            values.nativeLanguages?.map((language) => (typeof language === "string" ? language : language.value)) ??
            currentJobPosition.nativeLanguages ??
            [],
          requiredDocuments:
            values.requiredDocuments?.map((document) => (typeof document === "string" ? document : document?.value)) ??
            currentJobPosition.requiredDocuments ??
            [],
          otherBenefits: values.otherBenefits ? values.otherBenefits : (currentJobPosition.otherBenefits ?? []),
          daysOff:
            values.daysOff?.map((day) => (typeof day === "string" ? day : day.value)) ??
            currentJobPosition.daysOff ??
            [],
          employerQuestions: values.employerQuestions ?? currentJobPosition.employerQuestions ?? [],
          cities:
            values.cities?.map((city) => city.id) ??
            currentJobPosition.cities.edges.map((city) => {
              if (city?.node?.id) return city?.node?.id;
              return null;
            }) ??
            [],
          hiringInCanada:
            values.hiringInCanada === undefined ? currentJobPosition.hiringInCanada : values.hiringInCanada,
          locationType: values.locationType?.value ?? currentJobPosition.locationType,
          contractType: values.contractType?.value ?? currentJobPosition.contractType,
          paymentTerm: values.paymentTerm?.value ?? currentJobPosition.paymentTerm,
          workingEndAt: values.workingEndAt?.value ?? currentJobPosition.workingEndAt,
          workingStartAt: values.workingStartAt?.value ?? currentJobPosition.workingStartAt,
        },
        componentName: "UpdateJobPosition",
        actionName: "updateJobPosition",
        onSuccess: () => {
          handleNextStep();
        },
        successMessage: "Job post updated successfully",
        fallbackErrorMessage: "Failed to update job post",
        extraErrorContext: {
          organizationId: selectedOrganization.id,
          jobPositionId: id,
        },
      });
    } catch {
      toast.error("Failed to fetch job post data");
    }
  };

  return {
    handleSubmit,
    loading,
  };
};
